import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useCheckout } from 'lib/modules/checkout'
import { useCustomer } from 'lib/core/hooks'
import { DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import {
  setApplePayPaymentMethodOnCartMutation,
  SetVaultPaymentMethodOnCartResponse
} from '../utils'
import { getCommerceHubApplePayConfig } from '../config'

declare global {
  interface Window {
    ApplePaySession?: any
  }
}

interface PlaceApplePayOrderParams {
  onError: (e: Error) => void
}

const APPLE_PAY_SESSION_VERSION = 3

export const useCommerceHubApplePay = () => {
  const { cartId, orderTotal, lockCheckout, unlockCheckout, placeOrder } = useCheckout()
  const { isLoggedIn } = useCustomer()
  const [available, setAvailable] = useState(false)
  // A credential can only be stored against a customer account - the backend
  // ignores the flag for guests, so the opt-in is not offered to them either.
  const [saveCard, setSaveCard] = useState(false)
  const config = getCommerceHubApplePayConfig()

  // Eligibility: window.ApplePaySession is only present in Safari/WebKit - a
  // synchronous, one-time check (no polling needed, unlike the Google Pay SDK-load).
  useEffect(() => {
    try {
      setAvailable(Boolean(window.ApplePaySession?.canMakePayments()))
    } catch (e) {
      setAvailable(false)
    }
  }, [])

  const [setApplePayOnCart] = useMutation<{
    setPaymentMethodOnCart: SetVaultPaymentMethodOnCartResponse
  }>(setApplePayPaymentMethodOnCartMutation)

  const validateMerchant = async (validationURL: string) => {
    if (!config.validationUrl) {
      // GAP (architecture §3 / FONTOS.md): the hdsg-backend merchant-validation
      // proxy endpoint doesn't exist yet - fail gracefully instead of throwing
      // an unhandled error into ApplePaySession.
      throw new Error('Apple Pay merchant validation endpoint is not configured')
    }

    const response = await fetch(config.validationUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ validationURL })
    })

    if (!response.ok) {
      throw new Error('Apple Pay merchant validation failed')
    }

    return response.json()
  }

  /**
   * NOTE (architecture §7 Q6): must be called directly from within a user-gesture
   * click handler - ApplePaySession.begin() throws InvalidAccessError otherwise.
   * The component wires this to its own button's onClick (not to the generic
   * `usePaymentMethod` Redux-driven "Place Order" flow).
   */
  const placeApplePayOrder = (params: PlaceApplePayOrderParams) => {
    if (!window.ApplePaySession) {
      params.onError(new Error('ApplePaySession is not available in this browser'))
      return
    }

    const request = {
      countryCode: config.countryCode,
      currencyCode: DEFAULT_CURRENCY_CODE,
      supportedNetworks: config.networks,
      merchantCapabilities: config.capabilities,
      total: {
        label: config.displayName,
        amount: orderTotal.toFixed(2)
      }
    }

    const session = new window.ApplePaySession(APPLE_PAY_SESSION_VERSION, request)

    session.onvalidatemerchant = async (event: { validationURL: string }) => {
      try {
        const merchantSession = await validateMerchant(event.validationURL)
        session.completeMerchantValidation(merchantSession)
      } catch (e) {
        session.abort()
        unlockCheckout()
        params.onError(e instanceof Error ? e : new Error('Apple Pay merchant validation failed'))
      }
    }

    session.onpaymentauthorized = async (event: { payment: { token: any } }) => {
      try {
        lockCheckout()
        await setApplePayOnCart({
          variables: {
            cartId,
            paymentData: JSON.stringify(event.payment.token),
            displayName: config.displayName,
            network: event.payment.token?.paymentMethod?.network,
            transactionId: event.payment.token?.transactionIdentifier,
            saveCard: isLoggedIn && saveCard
          }
        })

        session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
        unlockCheckout()
        placeOrder()
      } catch (e) {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE)
        unlockCheckout()
        params.onError(e instanceof Error ? e : new Error('Apple Pay payment failed'))
      }
    }

    session.oncancel = () => {
      unlockCheckout()
    }

    session.begin()
  }

  return {
    available,
    canSaveCard: isLoggedIn,
    saveCard,
    setSaveCard,
    placeApplePayOrder
  }
}
